/**
 * PII sanitizers for log payloads.
 *
 * The Confluence spec mandates that logs MUST NOT contain PII. These helpers
 * scrub the most common patterns (emails, phone numbers, bearer tokens) from
 * arbitrary strings before they leave the application.
 */

const REDACTED = '[REDACTED]';

const DEFAULT_MAX_LENGTH = 500;

const REDACTION_PATTERNS: RegExp[] = [
    // Authorization bearer tokens (must run before generic patterns so the
    // token value is not partially eaten by another rule first).
    /\bBearer\s+[A-Za-z0-9._\-+/=]+/gi,
    // Email addresses.
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
    // Phone numbers (10+ digits, optional country code and common separators).
    /\+?\d[\d\s().-]{8,}\d/g,
];

/** Replace common PII patterns in `input` with `[REDACTED]`. */
export const sanitizeString = (input: string): string => {
    let output = input;
    for (const pattern of REDACTION_PATTERNS) {
        output = output.replace(pattern, REDACTED);
    }
    return output;
};

/**
 * Coerce a thrown value to a sanitized, length-capped string suitable for
 * the `errorMessage` field of an error log event.
 */
export const sanitizeErrorMessage = (err: unknown, maxLength: number = DEFAULT_MAX_LENGTH): string => {
    let message = '';
    if (err instanceof Error) {
        message = err.message;
    } else if (typeof err === 'string') {
        message = err;
    } else if (err !== null && err !== undefined) {
        try {
            message = JSON.stringify(err);
        } catch {
            message = String(err);
        }
    }
    const sanitized = sanitizeString(message);
    return sanitized.length > maxLength ? `${sanitized.slice(0, maxLength - 1)}…` : sanitized;
};
